import React, { Fragment, useCallback, useContext, useEffect, useRef, useState } from "react"
import { CampProps, CampingStyleProps, ReservationProps, SiteProps } from "../../../interfaces"
import Button from "../../ui/Button"
import moment, { Moment } from "moment"
import api from "../../../api2"
import { AdminContext } from "~/contexts/AdminContext"
import { ArrowLeftIcon, CalendarIcon, PlusIcon, PrinterIcon, UserIcon } from "@heroicons/react/24/solid"
import { formatName } from "~/utils/formatName"
import PreviewInvoiceToggle from "./PreviewInvoiceToggle"
import { formatCampAddress } from "~/utils/formatAddress"
import { PAYMENT_MODES, RESERVATION_KINDS, reservationKind, STATUSES } from "~/constants"
import * as Sentry from "@sentry/react"
import Spinner from "../../ui/Spinner"
import { FormProvider } from "~/contexts/FormContext"
import useAdminForm from "../../../hooks/useAdminForm"
import Form from "../../ui/Admin/Form"
import eSendingMode from "../../../enums/eSendingMode"
import SelectInput, { Option } from "../../ui/SelectInput"
import classNames from "../../../utils/classNames"
import { useCookies } from "react-cookie"
import { sendingInvoiceOptions } from "../../Reservations/sendingOptions"
import orders, { serviceRateMode } from "~/api2/orders"
import IOrderPreview from "../../../interfaces/IOrderPreview"
import { useReactToPrint } from "react-to-print"
import { useDebouncedCallback } from "use-debounce"
import IInvoice from "../../../interfaces/IInvoice"
import ILineItem from "../../../interfaces/ILineItem"
import { INVOICE_STATUSES } from "~/decorators/InvoiceDecorator"
import DiscountProps from "../../../interfaces/DiscountProps"
import parseDateTime from "../../../utils/parseDateTime"
import createMarkup from "../../../utils/createMarkup"
import { DayPickerSingleDateController } from "react-dates"
import { Menu, Transition } from "@headlessui/react"
import LineItemsTable from "./EditInvoice/components/LineItemsTable"
import MobileLineItemsTable from "./EditInvoice/components/MobileLineItemsTable"
import useSetTaxFees from "~/components/Admin/Invoices/hooks/useSetTaxFees"
import { cookieSendingModeInvoice } from "./constants"
import { initializeFormDataFor } from "~/components/Admin/Invoices/utils"
import { BoltIcon } from "@heroicons/react/20/solid"
import useApplyElectricFees from "~/components/Admin/Invoices/hooks/useApplyElectricFees"
import OrderSummary from "~/components/Admin/Orders/OrderSummary"
import isInvalidSendingMode from "~/components/Reservations/components/IsInvalidSendingMode"
import CheckboxWithLabelForForm from "../../ui/CheckboxWithLabelForForm"
import ProfileCard, { checkIsCamperEmpty } from "../Campers/Camper/ProfileCard"
import NewCamperModalContent from "../Campers/NewCamperModalContent"
import ICamper from "~/interfaces/ICamper"
import { ToggleFeesTypes } from "../Reservations/NewReservationModal/FeesAndTaxesSettings"
import { LINE_ITEM_KINDS } from "~/constants/line_item_kinds"
import api2 from "../../../api2"
import { usePreviews } from "../Reservations/ReservationHooks/usePreviews"
import { useOrder } from "../Reservations/ReservationHooks/useOrder"
import { useInvoicesPaymentsContext } from "~/contexts/InvoicePaymentsProvider"
import PortalHoverTip from "~/components/ui/PortalHoverTip"
import CookieWrapper from "~/components/helpers/CookieWrapper"
import { ccCookie } from "~/constants/cookies"

export interface ILineItemLegacy {
  id?: string
  is_description_read_only?: boolean
  is_unit_price_read_only?: boolean
  is_quantity_read_only?: boolean
  description: string
  unit_price: number
  quantity: number
  total_price: number
}

export type ILineItemField = Omit<ILineItem, "unit_amount_in_cents" | "total_amount_in_cents"> & {
  unit_amount: number
  total_amount: number
  is_hidden?: boolean
}

export interface IFormData {
  electricity_fee: number
  last_meter_reading: number
  current_meter_reading: number
  to_name: string
  to_email: string
  to_phone: string
  to_address: string
  memo: string
  items: ILineItemField[]
  sending_mode: eSendingMode
  is_displaying_occurrences?: boolean
  due_date: string
  is_credit_card_fee_enabled: boolean
  is_tax_fee_enabled: boolean
  credit_card_rate?: number
}

interface ComponentProps {
  invoice: IInvoice | undefined
  setSelectedInvoice?: (invoice: IInvoice) => any
  updateLocalInvoices?: (invoices?: IInvoice[]) => void
  onSuccess?: (invoice: IInvoice) => any
  campingStyle?: CampingStyleProps
  site: SiteProps | undefined
  isProcessing?: boolean
  disabled?: boolean
  className?: string
  customerView?: boolean
  isHidingBackButton?: boolean
  camp?: CampProps
  setCamp?: (camp: CampProps) => any
  cookies?: any
  printRef?: React.MutableRefObject<any>
  isForPrint?: boolean
  hasPageBreak?: boolean
  setIsSlideoverOpen?: (v: boolean) => void
  isInvoicePage?: boolean
  hasPendingPayments?: boolean
}

const EditInvoice = (props: ComponentProps) => {
  const { camp: adminCamp } = useContext(AdminContext)
  const camp = props.camp || adminCamp
  const campingStyles = camp.camping_styles
  const campingStyle = props.campingStyle || campingStyles?.find((cs) => props.site?.camping_style_id === cs.id)
  const [cookies] = useCookies([cookieSendingModeInvoice])
  const [resource, setResource] = useState<IFormData | null>(
    props.invoice ? initializeFormDataFor(props.invoice, props.site, camp, !!props.customerView, cookies) : null,
  )
  const campDiscounts = camp.discounts
  const campingStyleDiscounts = campingStyle?.discounts ? campingStyle?.discounts : []
  const availableDiscounts = [...campDiscounts, ...campingStyleDiscounts]

  useEffect(() => {
    if (props.invoice) {
      setResource(initializeFormDataFor(props.invoice, props.site, camp, !!props.customerView, cookies))
    }
  }, [props.invoice, props.site, camp, props.customerView, cookies])

  if (!props.invoice || !resource) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Spinner size={5} />
        <p className="mt-2 text-sm text-gray-500">Loading invoice data...</p>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full pb-8 max-w-8xl px-4 sm:px-8 mdlg:px-12 lg:px-16 xl:px-24">
      <FormProvider init={resource} removeWarnOnNavigate={props.isForPrint}>
        <InvoiceForm
          camp={camp}
          customerView={!!props.customerView}
          invoice={props.invoice}
          site={props.site}
          campingStyle={campingStyle}
          isHidingBackButton={props.isHidingBackButton}
          setSelectedInvoice={props.setSelectedInvoice}
          onSuccess={props.onSuccess}
          updateLocalInvoices={props.updateLocalInvoices}
          disabled={props.disabled}
          isProcessing={props.isProcessing}
          availableDiscounts={availableDiscounts}
          isForPrint={props.isForPrint}
          hasPageBreak={props.hasPageBreak}
          hasPendingPayments={props.hasPendingPayments}
          isInvoicePage={props.isInvoicePage}
        />
      </FormProvider>
    </div>
  )
}

const AddressField = ({
  label,
  name,
  address,
  handleNameChange,
  handleAddressChange,
  textOnly,
  className,
}: {
  label: string
  name: string
  address: string
  handleNameChange?: any
  handleAddressChange?: any
  textOnly?: boolean
  className?: string
}) => {
  return (
    <div className={className}>
      <label className="block mb-1 text-baselg font-bold">{label}</label>
      {textOnly ? (
        <>
          <span className="block text-base">{name}</span>
          <span className="block text-base">{address}</span>
        </>
      ) : (
        <>
          <input
            type="text"
            placeholder="Name"
            value={name}
            onChange={handleNameChange}
            className="w-full p-2 border rounded mb-2"
            data-testid="name-input"
          />
          <textarea
            rows={2}
            placeholder="Address"
            value={address}
            onChange={handleAddressChange}
            className="w-full p-2 border rounded"
            data-testid="address-input"
          />
        </>
      )}
    </div>
  )
}

const formatDateRange = (start_date: string, end_date: string, timezone: string) => {
  const startDate = parseDateTime(start_date, timezone, { asMoment: true })
  const endDate = parseDateTime(end_date, timezone, { asMoment: true })

  return startDate?.isSame(moment(), "year") && endDate?.isSame(moment(), "year")
    ? `${startDate?.format("MMM DD")} - ${endDate?.format("MMM DD")}`
    : `${startDate?.format("MMM DD YYYY")} - ${endDate?.format("MMM DD, YYYY")}`
}

export const InvoiceForm = ({
  camp,
  customerView,
  invoice,
  site,
  campingStyle,
  isHidingBackButton,
  setSelectedInvoice,
  onSuccess,
  updateLocalInvoices,
  disabled,
  isProcessing,
  availableDiscounts,
  isForPrint,
  hasPageBreak,
  isInvoicePage,
  hasPendingPayments,
}: {
  camp: CampProps
  customerView: boolean
  invoice: IInvoice
  site: SiteProps
  campingStyle: CampingStyleProps
  isHidingBackButton: boolean
  setSelectedInvoice: (invoice: IInvoice) => void
  onSuccess: (invoice: IInvoice) => any
  updateLocalInvoices: (invoices?: IInvoice[]) => void
  disabled: boolean
  isProcessing: boolean
  availableDiscounts: DiscountProps[]
  isForPrint?: boolean
  hasPageBreak?: boolean
  isInvoicePage?: boolean
  hasPendingPayments?: boolean
}) => {
  const { setValue, resource, watch, updateDefaults, reset, getValues } = useAdminForm<IFormData>()
  const {
    updateLocalReservations,
    showNotification,
    showPaymentModal,
    orderInvoice,
    showModal,
    setIsSlideoverOpen,
    setSelectedCamper,
    selectedCamper,
    selectedReservation,
  } = useContext(AdminContext)
  const { handleClickPayOnline } = useInvoicesPaymentsContext()
  const [cookies, setCookie] = useCookies([cookieSendingModeInvoice])
  const [camper, setCamper] = useState<ICamper>(selectedCamper || invoice.reservation?.camper)
  const [isLoading, setIsLoading] = useState(false)
  const [isShowingCc, setIsShowingCc] = useState(false)
  const [isPreview, setIsPreview] = useState(customerView)
  const [isLoadingPayOnline, setIsLoadingPayOnline] = useState(false)
  const items = watch("items")
  const previews = usePreviews({
    serviceRate: serviceRateMode.payment_link,
    isPaid: selectedReservation?.status === STATUSES.PAID,
    orderId: selectedReservation?.order_id,
    campingStyleId: selectedReservation?.selected_camping_style_id || selectedReservation?.site?.camping_style_id,
    discount_type: selectedReservation?.discount_type,
  })
  const order = useOrder({ orderId: selectedReservation?.order_id })

  const hasMeterLocationId = !!(
    selectedReservation?.site?.wild_energy_meter_location_id ??
    invoice?.invoice?.reservation?.site?.wild_energy_meter_location_id
  )

  const isInitiatedTaxFee = !!invoice?.order?.tax_fee_in_cents

  const isInitiatedCreditCardFee =
    !!orderInvoice?.data?.order?.credit_card_fee_in_cents ||
    !!invoice?.order?.credit_card_fee_in_cents ||
    !!invoice?.order?.credit_card_rate

  const isCreditCardFeeEnabled = watch("is_credit_card_fee_enabled") ?? isInitiatedCreditCardFee
  const isTaxFeeEnabled = watch("is_tax_fee_enabled") ?? isInitiatedTaxFee

  const [sendingMode, setSendingMode] = useState(
    resource.sending_mode || (cookies && cookies[cookieSendingModeInvoice]) || eSendingMode.EMAIL,
  )
  const due_date = watch("due_date")

  const targetRef = useRef(null)
  const { handleApplyFees } = useApplyElectricFees()

  const handleClickSave = () => {
    updateInvoice(false)
  }

  const handleClickSaveAndApplyElectricFees = useCallback(async () => {
    setIsLoading(true)
    try {
      // Apply electric fees
      await handleApplyFees(invoice)
      await updateInvoice(false, true)
    } catch (error) {
      console.error("Error applying electric fees and saving invoice:", error)
      showNotification({
        type: "error",
        title: "Error",
        description: "Failed to apply electric fees and save invoice.",
      })
    } finally {
      setIsLoading(false)
    }
  }, [invoice, handleApplyFees, showNotification])

  const handleClickSaveAndTakePayment = async () => {
    await updateInvoice(false)
    showPaymentModal(invoice?.order?.unpaid_total_in_cents, {
      reservation: invoice?.reservation,
      closeCallback: () => refreshInvoice(invoice?.id),
    })
  }

  const refreshInvoice = (id: string) => {
    console.log("refetching invoice", id)
    api.Invoices.find(camp.id, id)
      .then(({ invoice: updatedInvoice }) => {
        console.log("updated invoice", updatedInvoice)
        setSelectedInvoice(updatedInvoice)
        updateLocalReservations([updatedInvoice.reservation])
      })
      .catch((err) => {
        console.error(err)
        showNotification({
          type: "error",
          title: "Oops",
          description: err.message,
        })
      })
  }

  const handleClickSendInvoice = () => {
    updateInvoice(true)
  }

  const updateInvoice = async (isSendingInvoice: boolean, avoidOnSuccess?: boolean) => {
    setIsLoading(true)

    let mInvoice = invoice

    if (!invoice?.id) {
      try {
        const reservationId = items.find((item) => item.reservation_id)?.reservation_id
        const { invoice: newInvoice } = await api.Invoices.createForReservation(
          reservationId,
          isCreditCardFeeEnabled,
          isTaxFeeEnabled,
        )
        setSelectedInvoice(newInvoice)
        mInvoice = newInvoice
      } catch (err) {
        console.log("error creating new invoice", err)
        Sentry.captureException(err)
        showNotification({
          type: "error",
          title: "Oops",
          description: err.message,
        })
        setIsLoading(false)
        return
      }
    }

    const newSendDate = isSendingInvoice ? moment() : null
    console.log("updating invoice", mInvoice.id, camp.id)
    return api.Invoices.update(mInvoice.id, camp.id, {
      isSending: isSendingInvoice,
      sendingMode: sendingMode || resource.sending_mode || eSendingMode.EMAIL,
      admin_email: isShowingCc ? camp.email : null,
      sendDate: newSendDate
        ? newSendDate
        : invoice.send_date
          ? moment(parseDateTime(invoice.send_date).toJSDate())
          : null,
      dueDate: resource.due_date ? parseDateTime(resource.due_date, camp.timezone, { asMoment: true }) : null,
      recipientEmail: resource.to_email,
      recipientPhone: resource.to_phone,
      recipientName: resource.to_name,
      recipientAddress: resource.to_address,
      memo: resource.memo,
      lastMeterReading: getValues("last_meter_reading"),
      meterReading: getValues("current_meter_reading"),
      isCreditCardFeeEnabled: isCreditCardFeeEnabled,
      lineItemsAttributes: getValues("items").map((item) => ({
        id: item.id,
        kind: item.kind === "custom" ? "basic" : item.kind,
        label: item.label,
        totalAmountInCents: item.reservation_id ? Number(item.total_amount) * 100.0 : null,
        unitAmountInCents: item.reservation_id ? null : Number(item.unit_amount) * 100.0,
        quantity: item.quantity,
        occurrences: item.occurrences,
        occurrenceUnit: item.occurrence_unit,
        isTaxFeeEnabled: isTaxFeeEnabled,
        reservationId: item.reservation_id,
        addOnId: item.add_on_id,
        isAttachedToReservation: !!item.parent_line_item_id,
        is_updating_series: item.is_updating_series,
      })),
    })
      .then(({ invoice: updatedInvoice }) => {
        orderInvoice.refetch()

        const updatedReservation = { ...updatedInvoice.reservation, order: updatedInvoice.order }
        updateLocalReservations([updatedReservation])
        updateLocalInvoiceData(updatedInvoice)
        updateLocalFormData(updatedInvoice)
        setIsLoading(false)
        if (onSuccess && !avoidOnSuccess) onSuccess(updatedInvoice)
        showNotification({
          type: "success",
          title: isSendingInvoice ? "Invoice sent" : "Invoice updated",
          description: isSendingInvoice
            ? `Invoice ${updatedInvoice.hash_id} has been sent`
            : `Invoice ${updatedInvoice.hash_id} has been updated`,
        })
      })
      .catch((err) => {
        console.error(err)
        setIsLoading(false)
        showNotification({
          type: "error",
          title: "Oops",
          description: err.message,
        })
      })
      .finally(() => {
        order.refetch()
        previews.refetch()
      })
  }

  const updateLocalInvoiceData = (updated: IInvoice) => {
    if (updateLocalInvoices) updateLocalInvoices([updated])
    if (setSelectedInvoice) setSelectedInvoice(updated)
  }

  const updateLocalFormData = (updated: IInvoice) => {
    const formData = initializeFormDataFor(updated, site, camp, isPreview, cookies)
    updateDefaults(formData)
    reset(formData)
  }

  const addItem = () => {
    setValue("items", [
      ...items,
      {
        id: null,
        label: "",
        unit_amount: 0,
        quantity: 1,
        total_amount: 0,
        kind: LINE_ITEM_KINDS.CUSTOM,
        tax_rate: items[0]?.tax_rate,
        order_id: invoice?.order_id,
        occurrences: null,
        occurrence_unit: null,
        add_on_id: null,
        reservation_id: null,
        parent_line_item_id: null,
        discount_id: null,
        is_updating_series: false,
        is_always_applied: false,
      },
    ])
  }

  const onClickEditCamper = (setIsSlideoverOpen: (v) => Boolean) => {
    setIsSlideoverOpen?.(false)
    showModal(
      <NewCamperModalContent
        reservationId={invoice.reservation?.id}
        camper={camper}
        onSuccess={(updatedCamper) => {
          setCamper(updatedCamper)
          selectedReservation && setSelectedCamper?.(updatedCamper)
          updateLocalInvoices?.()
        }}
        existingOrderId={invoice.order?.id || invoice.reservation?.order_id || order?.data?.order?.id}
      />,
      false,
      "max-w-4xl",
      {
        closeCallback: () => {
          setIsSlideoverOpen?.(true)
        },
      },
    )
  }

  useEffect(() => {
    !(sendingMode === eSendingMode.EMAIL || sendingMode === eSendingMode.EMAIL_AND_TEXT) && setIsShowingCc(false)
  }, [sendingMode])

  useEffect(() => {
    if (customerView && !selectedCamper && !invoice.reservation?.camper && invoice.order?.camper_id && !isInvoicePage) {
      api2.Camps.findCamper(invoice.order?.camper_id).then((res) => setCamper(res.camper))
    } else if (isInvoicePage && invoice.reservation?.camper) {
      setCamper(invoice.reservation.camper as ICamper)
    }
  }, [isInvoicePage])

  const handlePrint = useReactToPrint({
    content: () => targetRef.current,
  })

  const isShowingSite = campingStyle?.is_showing_site_in_notifiers

  const isCamperEmpty = !camper || checkIsCamperEmpty(camper)

  return (
    <div>
      {customerView ? (
        <div className="mt-12"></div>
      ) : (
        <div className="flex items-center justify-start sm:justify-between mb-8 sm:mb-12">
          {isHidingBackButton ? (
            <div />
          ) : (
            <Button
              variant="white"
              className="border-none pl-2.5 pr-3"
              icon={<ArrowLeftIcon className="w-5 h-5" />}
              onClick={() => setSelectedInvoice(null)}
              dataTestId="back-button"
            >
              Back
            </Button>
          )}
          <div className="hidden sm:block text-2xl font-semibold">
            {(camper?.first_name || camper?.last_name) &&
              (invoice.id ? "Invoice for " + formatName(camper) : "New invoice for " + formatName(camper))}
          </div>

          <div
            className="bg-gray-100 p-3 rounded-md border-dashed border-2 border-gray-300"
            onClick={() => setIsPreview(!isPreview)}
          >
            <PreviewInvoiceToggle
              checked={isPreview}
              onChange={setIsPreview}
              name="isBookable"
              label="Preview"
              labelClassName="text-lg font-semibold"
            />
          </div>
        </div>
      )}
      <div className={classNames(isForPrint && "max-w-4xl", isForPrint && hasPageBreak && "pagebreak", "print-margin")}>
        <div className="flex gap-8 sm:gap-40 mdlg:gap-8 justify-between mb-6 sm:mb-12 flex-col sm:flex-row items-start">
          <div className="flex-1 max-w-sm">
            <AddressField
              label="From"
              name={camp.name}
              address={formatCampAddress(camp)}
              textOnly={true}
              className="mb-4"
            />

            {isPreview && !camper ? (
              <></>
            ) : (
              <div className="flex flex-col gap-2">
                <span className="block mb-1 text-baselg font-bold">To</span>
                {camper ? (
                  <ProfileCard
                    readOnly
                    camper={selectedCamper || camper}
                    isBlocked={false}
                    hidePaymentMethods
                    hideCardBorder
                    hideAdditionalInfo
                    hideAvatar
                  />
                ) : (
                  <></>
                )}
                {!customerView ? (
                  <Button
                    type="button"
                    variant="white"
                    size="small"
                    shadow="small"
                    className="flex items-center text-gray-600"
                    onClick={() => onClickEditCamper(setIsSlideoverOpen)}
                    dataTestId="edit-camper-button"
                  >
                    <UserIcon className="w-5 h-5 mr-2" />
                    <div>{isCamperEmpty ? "Add" : "Edit"} guest</div>
                  </Button>
                ) : (
                  <></>
                )}
              </div>
            )}
          </div>
          <div
            className={classNames(
              "flex-1 grid gap-x-2 grid-cols-2",
              moment(invoice.reservation?.start_date).isSame(moment(), "year") &&
                moment(invoice.reservation?.end_date).isSame(moment(), "year")
                ? "max-w-sm"
                : "max-w-md",
            )}
          >
            <label className="block mb-2 text-md font-bold">Invoice #</label>
            {customerView || isForPrint ? (
              <span className="text-base">{invoice.hash_id}</span>
            ) : (
              <a
                className="text-base hover:underline cursor-pointer"
                href={`${window.location.origin}/i/${invoice.hash_id}`}
                target="_blank"
              >
                {invoice.hash_id}
              </a>
            )}

            {((invoice?.reservation?.kind == RESERVATION_KINDS.RECURRING && invoice.sent_at) ||
              invoice?.reservation?.kind != RESERVATION_KINDS.RECURRING) && (
              <>
                <label className="block mb-2 text-md font-bold">Invoice Date</label>
                <span className="text-base">
                  {invoice.reservation?.kind == RESERVATION_KINDS.RECURRING
                    ? parseDateTime(invoice?.sent_at)?.toFormat(`MMMM dd, yyyy`)
                    : parseDateTime(invoice?.reservation?.created_at)?.toFormat(`MMMM dd, yyyy`)}
                </span>
              </>
            )}

            {(invoice?.reservation?.kind == RESERVATION_KINDS.RECURRING ||
              invoice?.reservation?.payment_mode == PAYMENT_MODES.PAYMENT_LINK) && (
              <>
                <label className={classNames("mb-2 text-md font-bold ", !isPreview && "mt-3.5")}>Due Date</label>
                {isPreview ? (
                  <span className="text-base">{parseDateTime(due_date, camp.timezone)?.toFormat(`MMMM dd, yyyy`)}</span>
                ) : (
                  <DatePicker
                    setSelectedDate={(e) => setValue("due_date", e.format())}
                    selectedDate={parseDateTime(due_date, camp.timezone, { asMoment: true })}
                    dataTestId="due-date-picker"
                  />
                )}
              </>
            )}

            <label className="block mb-2 text-md font-bold">Dates</label>
            <span className="text-base">
              {formatDateRange(invoice.reservation?.start_date, invoice.reservation?.end_date, camp.timezone)}
            </span>

            {isShowingSite && (
              <>
                <label className="block mb-2 text-md font-bold">Site</label>
                <span className="text-base">{site?.name}</span>
              </>
            )}

            {([RESERVATION_KINDS.RECURRING, RESERVATION_KINDS.FINAL_INVOICE] as reservationKind[]).includes(
              invoice?.reservation?.kind,
            ) && (
              <>
                <label className={`block mb-2 text-md font-bold ${!isPreview && "mt-2.5"}`}>Last meter reading</label>
                <div className="relative w-full mb-2">
                  <Form.NumberInput
                    field="last_meter_reading"
                    suffix="KWh"
                    asText={isPreview}
                    disabled={hasMeterLocationId}
                  />
                </div>

                <label className={`block mb-2 text-md font-bold ${!isPreview && "mt-2.5"}`}>
                  Current meter reading
                </label>
                <div className="relative w-full">
                  <Form.NumberInput
                    field="current_meter_reading"
                    suffix="KWh"
                    asText={isPreview}
                    disabled={hasMeterLocationId}
                  />
                </div>
                <div className="col-span-2 flex justify-end">
                  {!isForPrint && !isPreview && (
                    <Button
                      className="mt-2 flex items-center justify-between gap-3"
                      variant="greenOutline"
                      onClick={handleClickSaveAndApplyElectricFees}
                      dataTestId="apply-electric-fees-button"
                    >
                      <BoltIcon className="w-4 h-4" />
                      {`${(hasMeterLocationId && "Fetch reading") || "Save"} and apply electric fees`}
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        <LineItemsTable
          resource={resource}
          items={items}
          isForPrint={isForPrint}
          isPreview={isPreview}
          isRecurring={invoice.reservation?.kind == RESERVATION_KINDS.RECURRING}
          camp={camp}
          site={site}
        />
        <MobileLineItemsTable
          resource={resource}
          items={items}
          isForPrint={isForPrint}
          isPreview={isPreview}
          site={site}
        />
        <Button
          onClick={addItem}
          variant="white"
          icon={<PlusIcon className="w-5 h-5" />}
          className={classNames(isPreview && "hidden", "mt-2 lg:mt-0")}
          dataTestId="add-item-button"
        >
          Add Item
        </Button>
        <div className="flex justify-between mt-8 flex-col mdlg:flex-row gap-y-5">
          <div className="flex-1 mr-4 max-w-lg">
            {isPreview ? (
              resource.memo && (
                <>
                  <label className="block mb-2 font-bold text-baselg">Memo</label>
                  <span
                    className="text-base html-editor-container whitespace-pre-wrap"
                    dangerouslySetInnerHTML={createMarkup(resource.memo)}
                  ></span>
                </>
              )
            ) : (
              <>
                <label className="block mb-2 font-bold text-baselg">Memo</label>
                <Form.RichTextInput field="memo" className="mb-4" />
              </>
            )}
          </div>
          <Total
            invoice={invoice}
            site={site}
            camp={camp}
            isCustomerView={isPreview}
            discounts={availableDiscounts}
            campingStyle={campingStyle}
            isInvoicePage={isInvoicePage}
          />
        </div>
      </div>
      <div className="flex gap-8 flex-col">
        {isForPrint ? (
          <></>
        ) : customerView ? (
          <div className="flex flex-col justify-end mt-12 w-40 ml-auto">
            <div className="flex justify-end">
              <Button
                variant="white"
                size={window.innerWidth > 400 ? "large" : "normal"}
                className="custom-500:flex ml-0 mdlg:ml-4 flex-shrink-0 justify-self-end items-center mr-2 mdlg:mr-0"
                onClick={handlePrint}
                disabled={disabled || isProcessing || isLoading}
                dataTestId="print-button"
              >
                <PrinterIcon className="w-5 h-5 mr-2" />
                Print
              </Button>
              {!invoice?.reservation?.long_term_reservation_id && (
                <Button
                  variant="white"
                  size={window.innerWidth > 400 ? "large" : "normal"}
                  className="ml-4 flex-shrink-0 justify-self-end"
                  onClick={() => (window.location.href = `${window.origin}/r/${invoice?.hash_id}`)}
                  dataTestId="view-reservation-button"
                >
                  View Reservation Info
                </Button>
              )}
              {invoice?.status !== STATUSES.PAID && (
                <PortalHoverTip
                  content={
                    (hasPendingPayments && "Bank transfer in-progress") ||
                    (invoice?.order?.unpaid_total_in_cents === 0 && "Total must be above 0 to pay online") ||
                    ""
                  }
                  className="flex-shrink-0 justify-center flex items-center w-full ml-2"
                  testId="pay-online-tooltip-wrapper"
                >
                  <Button
                    variant="blue"
                    disabled={
                      isLoadingPayOnline ||
                      hasPendingPayments ||
                      invoice?.order?.unpaid_total_in_cents === 0 ||
                      !camp?.stripe_account_id
                    }
                    size={window.innerWidth > 400 ? "large" : "normal"}
                    className="flex-shrink-0 justify-center flex items-center w-full ml-2"
                    onClick={handleClickPayOnline}
                    dataTestId="pay-online-button"
                  >
                    {isLoadingPayOnline ? <Spinner size={4} /> : "Pay Online"}
                  </Button>
                </PortalHoverTip>
              )}
            </div>
            {invoice?.status !== STATUSES.PAID && camp?.stripe_account_id && (
              <div className="flex items-center w-full justify-end">
                <span className="text-red-500 font-medium pr-1">*</span>
                <span className="flex text-sm text-muted font-medium">Additional fees apply</span>
              </div>
            )}
          </div>
        ) : (
          <div className="flex justify-end mt-6 mdlg:mt-12 items-end flex-col mdlg:flex-row flex-wrap gap-y-4">
            <div className="flex items-center flex-col-reverse mdlg:flex-row-reverse mb-3 mdlg:mb-0">
              <SelectInput
                name="sendingOptions"
                large
                buttonSize="large"
                width="w-auto"
                options={sendingInvoiceOptions}
                selected={sendingInvoiceOptions.find((o) => o.value === sendingMode)}
                setSelected={(o: Option) => {
                  setCookie(cookieSendingModeInvoice, o.value)
                  setSendingMode(o.value)
                  setValue("sending_mode", o.value)
                }}
                warningText={
                  isInvalidSendingMode(
                    resource.to_email || camper?.email || "",
                    resource.to_phone || camper?.phone_number || "",
                    sendingMode,
                  ) && (
                    <div className="text-xs text-yellow-600 -mt-1.5">
                      {sendingMode === eSendingMode.EMAIL
                        ? "Will not send without a valid email"
                        : sendingMode === eSendingMode.TEXT
                          ? "Will not send without a valid phone number"
                          : "Will not send without a valid email & phone number"}
                    </div>
                  )
                }
              />
              {(sendingMode === eSendingMode.EMAIL || sendingMode === eSendingMode.EMAIL_AND_TEXT) && (
                <div className="flex items-center mdlg:mr-3 mb-2 mdlg:mb-0">
                  <CookieWrapper value={isShowingCc} setValue={setIsShowingCc} cookieName={ccCookie}>
                    <CheckboxWithLabelForForm
                      label={`cc ${camp.email}`}
                      value={isShowingCc}
                      setValue={setIsShowingCc}
                      labelClasses="text-sm whitespace-nowrap"
                    />
                  </CookieWrapper>
                </div>
              )}
            </div>
            <div className="flex">
              <Button
                variant="white"
                size={"normal"}
                className="ml-0 mdlg:ml-4 flex-shrink-0 justify-self-end flex items-center mr-2 mdlg:mr-0"
                onClick={() => {
                  const prevIsPreview = isPreview
                  setIsPreview(true)
                  handlePrint()
                  setIsPreview(prevIsPreview)
                }}
                disabled={disabled || isProcessing || isLoading}
                dataTestId="print-button"
              >
                <PrinterIcon className="w-5 h-5 mr-2" />
                Print
              </Button>
              <Button
                variant="white"
                size={"normal"}
                className="ml-0 mdlg:ml-4 flex-shrink-0 justify-self-end"
                onClick={handleClickSave}
                disabled={disabled || isProcessing || isLoading}
                withDropdown={[
                  {
                    label: "Save & take payment",
                    onClick: handleClickSaveAndTakePayment,
                  },
                ]}
                dataTestId="save-button"
              >
                Save
              </Button>
              <Button
                variant="blue"
                size={"normal"}
                className="ml-4 flex-shrink-0 justify-self-end"
                onClick={handleClickSendInvoice}
                disabled={
                  disabled ||
                  isProcessing ||
                  isLoading ||
                  isInvalidSendingMode(
                    resource.to_email || camper?.email || "",
                    resource.to_phone || camper?.phone_number || "",
                    sendingMode,
                  )
                }
                dataTestId="send-button"
              >
                Send now
              </Button>
            </div>
          </div>
        )}
      </div>
      <div ref={targetRef}>
        {!isForPrint && (
          <PrintableInvoiceForm
            invoice={invoice}
            camp={camp}
            site={site}
            campingStyle={campingStyle}
            availableDiscounts={availableDiscounts}
            isInvoicePage={isInvoicePage}
          />
        )}
      </div>
    </div>
  )
}

const PrintableInvoiceForm = ({ invoice, camp, site, campingStyle, availableDiscounts, isInvoicePage }) => {
  return (
    <div className="hidden print:block">
      <InvoiceForm
        customerView
        invoice={invoice}
        camp={camp}
        site={site}
        campingStyle={campingStyle}
        availableDiscounts={availableDiscounts}
        isHidingBackButton
        setSelectedInvoice={() => null}
        onSuccess={() => null}
        updateLocalInvoices={() => null}
        disabled
        isProcessing
        isForPrint
        isInvoicePage={isInvoicePage}
      />
    </div>
  )
}

const Total = ({
  invoice,
  site,
  camp,
  isCustomerView,
  discounts,
  campingStyle,
  isInvoicePage,
}: {
  invoice: IInvoice
  site: SiteProps
  camp: CampProps
  isCustomerView?: boolean
  discounts: DiscountProps[]
  campingStyle: CampingStyleProps
  isInvoicePage?: boolean
}) => {
  const { watch, setValue } = useAdminForm<IFormData>()
  const [preview, setPreview] = useState<IOrderPreview>()
  const [isPreview, setIsPreview] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const localItems = watch("items")
  const isLongTermAndHasElectricTaxRate =
    invoice.reservation?.kind === RESERVATION_KINDS.RECURRING && !!camp.electric_tax_rate

  const { taxFeeEnabled, creditCardFeeEnabled, TaxToggle } = useSetTaxFees({
    order: invoice?.order,
    campingStyle,
    onUpdateFees: (fees: Partial<Record<keyof ToggleFeesTypes, boolean>>) => {
      setValue("is_credit_card_fee_enabled", !!fees.is_credit_card_fee_enabled)
      setValue("is_tax_fee_enabled", isLongTermAndHasElectricTaxRate || !!fees.is_tax_enabled)
    },
    isLongTermElectric: isLongTermAndHasElectricTaxRate,
    isInvoice: true,
    isLongTerm: !!invoice?.reservation?.long_term_reservation_id,
  })

  const isPaid =
    (isCustomerView && invoice?.status === INVOICE_STATUSES.PAID) || (preview ?? invoice?.order)?.status === "paid"

  const debouncedPreview = useDebouncedCallback((items: ILineItemField[]) => {
    const discount = discounts?.find((d) => d.id === items?.find((li) => li.discount_id)?.discount_id)
    if (isPaid && invoice?.order) {
      api.Orders.orderAndPreviewFor(
        invoice?.order?.id,
        camp.id,
        invoice?.reservation?.selected_camping_style_id || site?.camping_style_id,
        serviceRateMode.payment_link,
        isPaid,
        discount?.slug,
      )
        .then(({ order: o, preview: p }) => {
          setPreview((o as IOrderPreview) ?? p)
          setIsPreview(false)
          setIsLoading(false)
        })
        .catch((err) => {
          console.error(err)
          setIsLoading(false)
        })
    } else if (invoice?.order) {
      api.Orders.createPreview({
        order_id: invoice.order.id,
        camp_id: camp.id,
        camping_style_id: invoice.reservation?.selected_camping_style_id || site?.camping_style_id,
        is_credit_card_fee_enabled: creditCardFeeEnabled,
        is_service_fee_enabled: true,
        service_rate_mode: serviceRateMode.payment_link,
        discount_slug: discount?.slug,
        line_items_attributes: ((items && items) || []).map((item) => ({
          id: item.id,
          kind: item.kind,
          label: item.label,
          total_amount_in_cents: item.reservation_id ? Number(item.total_amount) * 100.0 : null,
          unit_amount_in_cents: item.reservation_id ? null : Number(item.unit_amount) * 100.0,
          quantity: item.quantity,
          occurrences: item.occurrences,
          occurrence_unit: item.occurrence_unit,
          is_tax_fee_enabled: taxFeeEnabled,
          reservation_id: item.reservation_id,
          add_on_id: item.add_on_id,
          is_attached_to_reservation: !!item.parent_line_item_id,
        })),
      })
        .then(({ order }) => {
          setPreview(order.preview)
          setIsPreview(true)
          setIsLoading(false)
        })
        .catch((err) => {
          console.error(err)
          setIsLoading(false)
        })
    }
  }, 300)

  useEffect(() => {
    setIsLoading(true)
    debouncedPreview(localItems)
  }, [invoice, localItems?.reduce((acc, item) => acc + item.total_amount, 0), taxFeeEnabled, creditCardFeeEnabled])

  if (!preview) {
    return <></>
  }

  const taxRateLabel = campingStyle?.tax_rate
    ? campingStyle?.tax_rate_label
    : camp?.tax_rate
      ? camp?.tax_rate_label
      : "Tax"

  return (
    <div className="flex-1 max-w-sm ml-auto" style={{ minWidth: 250 }}>
      {!isCustomerView && !isPaid && (
        <div className="w-full flex justify-between items-center px-4">
          {isLoading && <Spinner size={5} />}
          {TaxToggle}
        </div>
      )}
      <OrderSummary
        order={preview}
        camp={camp}
        isPreview={isPreview}
        isInvoice
        noBackgroundOrBorder
        isInvoicePage={isInvoicePage}
        isRecurring={!!invoice?.reservation?.long_term_reservation_id}
        taxRateLabel={taxRateLabel}
      />
    </div>
  )
}

interface DatePickerProps {
  selectedDate: moment.Moment
  setSelectedDate: (value: moment.Moment) => void
  dataTestId?: string
}

const DatePicker = (props: DatePickerProps) => {
  const { camp } = useContext(AdminContext)

  return (
    <Menu as="div" className="relative block text-left z-10 mr-auto w-full mdlg:w-auto">
      <div>
        <Menu.Button
          data-testid={props.dataTestId}
          className={`justify-center whitespace-nowrap inline-flex items-center w-full border rounded-md mt-1 mdlg:mt-auto border-gray-300 shadow-sm px-4 py-2.5 bg-white font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2  focus:ring-offset-gray-100 open-sans noOutline`}
        >
          <CalendarIcon className="mr-3.5 -ml-1 mb-0.25 mt-0.25 h-5.5 w-5.5" aria-hidden="true" />
          {props.selectedDate?.format("MMMM Do")}
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="invoice-date-picker absolute rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
          <DayPickerSingleDateController
            noBorder={true}
            initialVisibleMonth={() => props.selectedDate}
            date={parseDateTime(props.selectedDate?.toISOString(), camp.timezone, { asMoment: true })}
            onDateChange={(date) => props.setSelectedDate(date)}
            isOutsideRange={(day: Moment) => false}
            focused={true}
            onFocusChange={(e) => true}
            numberOfMonths={1}
            hideKeyboardShortcutsPanel={true}
          />
        </Menu.Items>
      </Transition>
    </Menu>
  )
}

export default EditInvoice
